import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { CustomersMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { CircularProgress, Modal } from '@mui/material'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { TeamRole } from 'src/interfaces/team'
import { ManageableTeamRole } from './ManageableTeamRole'
import { CreateTeamRoleButton } from '../create-team-role/CreateTeamRoleButton'
import { useDispatch, useSelector } from 'react-redux'
import { DeleteTeamRole, FetchTeamPermissions, FetchTeamRoles } from 'src/constants/actionTypes'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { getTeamRoles } from 'src/selectors/team'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { useModalOpen } from 'src/hooks/useModalOpen'

const ManageTeamRolesComponent = () => {
  const dispatch = useDispatch()

  const teamRoles = useSelector(getTeamRoles)

  const { hasPermission } = useCheckPermissions()
  const {
    isOpen: isManageTeamRolesOpen,
    open: openManageTeamRoles,
    close: closeManageTeamRoles,
  } = useModalOpen()

  const [isLoading, setIsLoading] = useState(true)

  const container = useMemo(() => document.getElementById('root'), [])

  const fetchTeamRoles = useCallback(() => {
    setIsLoading(true)

    try {
      dispatch(FetchTeamRoles.request())
      dispatch(FetchTeamPermissions.request())
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refreshTeamRoles = useCallback(async (): Promise<void> => {
    await fetchTeamRoles()
  }, [])

  const handleDeleteTeamRole = useCallback(async (teamRole: TeamRole) => {
    setIsLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        DeleteTeamRole.request({
          teamRoleId: teamRole.id,
        }),
      )

      await refreshTeamRoles()

      showToast(`Team Role "${teamRole.name}" is deleted`, { variant: 'success' })
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleOpen = useCallback(() => {
    fetchTeamRoles()

    openManageTeamRoles()
  }, [fetchTeamRoles, openManageTeamRoles])

  useEffect(() => {
    fetchTeamRoles()
  }, [])

  return (
    <>
      <Button
        className={tw`mr-2`}
        color="white"
        size="small"
        leftIcon={<CustomersMinor width={20} />}
        onClick={handleOpen}
      >
        Manage roles
      </Button>
      <Modal
        container={container}
        open={Boolean(isManageTeamRolesOpen)}
        onClose={closeManageTeamRoles}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        css={tw`flex flex-row w-full h-full`}
      >
        <div css={tw`bg-white w-[400px] overflow-hidden max-h-full flex flex-col`}>
          <ModalHeader title="Manage roles" onClose={closeManageTeamRoles} />
          <div css={tw`relative flex flex-col h-full bg-white`}>
            <div css={tw`px-4 flex-1 overflow-auto pb-[96px]`}>
              {isLoading || !teamRoles ? (
                <div css={tw`flex items-center justify-center h-full`}>
                  <CircularProgress />
                </div>
              ) : (
                teamRoles.map((item) => (
                  <ManageableTeamRole
                    teamRole={item}
                    key={item.id}
                    onRemove={handleDeleteTeamRole}
                  />
                ))
              )}
            </div>
            {hasPermission(UserPermission.RolesEdit) && (
              <div
                css={tw`sticky bottom-0 p-4 border-solid border-0 border-t border-[#EDEDED] bg-white`}
              >
                <CreateTeamRoleButton refreshTeamRoles={refreshTeamRoles} />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  )
}

export const ManageTeamRoles = memo(ManageTeamRolesComponent)
